import { useEffect, useRef, useState, type ReactNode } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { agentLabel, api, getErrorMessage } from '../api/client';
import type { McpAuthRequest } from '../api/types';
import { loginRequest } from '../auth/msal';
import logoBig from '../logo_big.png';

/**
 * Consent for an MCP agent's sign-in, as on the wiki. /oauth/authorize parks the agent's request
 * and sends the browser here. This page signs the user in with Microsoft if needed, shows who is
 * asking, and on Allow returns the browser to the agent with an authorization code.
 */
export function McpConsent() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestId = params.get('request') ?? '';
  const signInStarted = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Start Microsoft sign-in without a click; MSAL brings the browser back to this URL.
  useEffect(() => {
    if (isAuthenticated || inProgress !== InteractionStatus.None || signInStarted.current) return;
    signInStarted.current = true;
    instance.loginRedirect(loginRequest).catch((err: unknown) => {
      signInStarted.current = false;
      setError(getErrorMessage(err));
    });
  }, [isAuthenticated, inProgress, instance]);

  const request = useQuery({
    queryKey: ['mcp-request', requestId],
    queryFn: () => api<McpAuthRequest>(`/api/oauth/requests/${encodeURIComponent(requestId)}`),
    enabled: isAuthenticated && requestId !== '',
    retry: false,
  });

  async function answer(action: 'approve' | 'deny'): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ redirect: string | null }>(
        `/api/oauth/requests/${encodeURIComponent(requestId)}/${action}`,
        { method: 'POST' },
      );
      if (res.redirect) {
        window.location.assign(res.redirect);
        return;
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setBusy(false);
      setError(getErrorMessage(err));
    }
  }

  let body: ReactNode = null;
  if (!requestId) {
    body = <p className="text-sm text-gray-700">This page finishes an agent's sign-in. Start it from your MCP client.</p>;
  } else if (!isAuthenticated) {
    body = error ? (
      <>
        <p className="text-sm text-red-600">{error}</p>
        <button
          className="mt-4 rounded bg-navy px-4 py-1.5 text-sm font-semibold text-white"
          onClick={() => void instance.loginRedirect(loginRequest)}
        >
          Sign in with Microsoft
        </button>
      </>
    ) : (
      <p className="text-sm text-gray-700">Signing you in with Microsoft…</p>
    );
  } else if (request.isPending) {
    body = <p className="text-sm text-gray-700">Checking the agent's request…</p>;
  } else if (request.isError) {
    body = <p className="text-sm text-red-600">{getErrorMessage(request.error)}</p>;
  } else {
    const req = request.data;
    body = (
      <>
        <p className="text-sm">
          Allow <strong>{agentLabel(req)}</strong> to use FABS as <strong>{req.user.email}</strong>?
        </p>
        <p className="mt-2 text-sm text-gray-600">
          It gets the access you have{req.user.isAdmin ? '' : ': the buckets and paths your grants allow'}, for{' '}
          {req.sessionDays} days or until you disconnect it on the Agents page.
        </p>
        {req.user.isAdmin ? (
          <p className="mt-3 rounded bg-amber-50 p-2 text-xs text-amber-900">
            You are an admin, so it can read, overwrite, and delete every object in every bucket, and change who has
            access to them.
          </p>
        ) : null}
        <p className="mt-3 break-all font-mono text-xs text-gray-500">{req.redirectUri}</p>
        {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            className="rounded border border-gray-300 px-4 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={busy}
            onClick={() => void answer('deny')}
          >
            Cancel
          </button>
          <button
            className="rounded bg-navy px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-50"
            disabled={busy}
            onClick={() => void answer('approve')}
          >
            Allow
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center bg-[#162866] px-4">
      <img src={logoBig} alt="FABS — Fantastically Awesome Blob Storage" className="mb-6 w-64 max-w-[70%]" />
      <div className="w-full max-w-md rounded bg-white p-6 shadow-xl">
        <h1 className="mb-3 text-lg font-bold text-navy">Connect an agent</h1>
        {body}
      </div>
    </div>
  );
}
